/**
 * ImageCropModal - Modal dialog for cropping images before upload
 * Uses react-easy-crop for the cropping interface
 */

import React, { useState, useCallback } from 'react';
import Cropper, { Area, Point } from 'react-easy-crop';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Slider,
  Typography,
  Stack,
  CircularProgress,
  IconButton,
  alpha,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import CropIcon from '@mui/icons-material/Crop';
import { StationImageType } from './types';
import {
  processImageForUpload,
  compressImageOnly,
  getAspectRatioForType,
  getOutputDimensionsLabel,
} from './utils/cropImage';

export interface ImageCropModalProps {
  open: boolean;
  imageSrc: string;
  imageType: StationImageType;
  originalFile: File;
  onComplete: (processedFile: File) => void;
  onCancel: () => void;
  allowSkip?: boolean; // Only true for gallery images
}

const ImageCropModal: React.FC<ImageCropModalProps> = ({
  open,
  imageSrc,
  imageType,
  originalFile,
  onComplete,
  onCancel,
  allowSkip = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const aspectRatio = getAspectRatioForType(imageType);
  const outputLabel = getOutputDimensionsLabel(imageType);

  const onCropComplete = useCallback((_croppedArea: Area, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleCropAndContinue = async () => {
    if (!croppedAreaPixels) return;

    setIsProcessing(true);
    try {
      const processedFile = await processImageForUpload(
        imageSrc,
        imageType,
        croppedAreaPixels,
        originalFile
      );
      onComplete(processedFile);
    } catch (error) {
      console.error('Error processing image:', error);
      // Could add error handling UI here
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSkip = async () => {
    setIsProcessing(true);
    try {
      // Just compress without cropping
      const compressedFile = await compressImageOnly(originalFile);
      onComplete(compressedFile);
    } catch (error) {
      console.error('Error compressing image:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleZoomIn = () => {
    setZoom((prev) => Math.min(prev + 0.1, 3));
  };

  const handleZoomOut = () => {
    setZoom((prev) => Math.max(prev - 0.1, 1));
  };

  const getTitle = () => {
    switch (imageType) {
      case 'icon':
        return 'Crop Station Icon';
      case 'banner':
        return 'Crop Station Banner';
      case 'gallery':
        return 'Crop Image (Optional)';
      default:
        return 'Crop Image';
    }
  };

  const getAspectRatioLabel = () => {
    switch (imageType) {
      case 'icon':
        return '1:1 (Square)';
      case 'banner':
        return '3:1 (Wide)';
      case 'gallery':
        return 'Free';
      default:
        return '';
    }
  };

  return (
    <Dialog
      open={open}
      onClose={isProcessing ? undefined : onCancel}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          bgcolor: 'background.paper',
          minHeight: isMobile ? '100%' : 600,
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: `1px solid ${theme.palette.divider}`,
          py: 1.5,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <CropIcon color="primary" />
          <Typography variant="h6" component="span">
            {getTitle()}
          </Typography>
        </Stack>
        <IconButton
          onClick={onCancel}
          disabled={isProcessing}
          size="small"
          sx={{ color: 'text.secondary' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* Crop Area */}
      <DialogContent
        sx={{
          p: 0,
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Cropper Container */}
        <Box
          sx={{
            position: 'relative',
            flex: 1,
            minHeight: isMobile ? 300 : 400,
            bgcolor: alpha(theme.palette.common.black, 0.9),
          }}
        >
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={aspectRatio}
            onCropChange={setCrop}
            onCropComplete={onCropComplete}
            onZoomChange={setZoom}
            style={{
              containerStyle: {
                width: '100%',
                height: '100%',
              },
            }}
          />

          {/* Processing Overlay */}
          {isProcessing && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: alpha(theme.palette.common.black, 0.7),
                zIndex: 10,
              }}
            >
              <CircularProgress sx={{ color: 'white', mb: 2 }} />
              <Typography sx={{ color: 'white' }}>Processing image...</Typography>
            </Box>
          )}
        </Box>

        {/* Controls */}
        <Box
          sx={{
            p: 2,
            borderTop: `1px solid ${theme.palette.divider}`,
            bgcolor: 'background.default',
          }}
        >
          {/* Zoom Control */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={2}
            sx={{ maxWidth: 400, mx: 'auto' }}
          >
            <IconButton onClick={handleZoomOut} disabled={zoom <= 1 || isProcessing} size="small">
              <ZoomOutIcon />
            </IconButton>
            <Slider
              value={zoom}
              min={1}
              max={3}
              step={0.1}
              onChange={(_, value) => setZoom(value as number)}
              disabled={isProcessing}
              sx={{ flex: 1 }}
            />
            <IconButton onClick={handleZoomIn} disabled={zoom >= 3 || isProcessing} size="small">
              <ZoomInIcon />
            </IconButton>
            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 45 }}>
              {zoom.toFixed(1)}x
            </Typography>
          </Stack>

          {/* Info */}
          <Stack
            direction="row"
            justifyContent="center"
            spacing={3}
            sx={{ mt: 2 }}
          >
            <Typography variant="caption" color="text.secondary">
              Aspect ratio: <strong>{getAspectRatioLabel()}</strong>
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Output: <strong>{outputLabel}</strong>
            </Typography>
          </Stack>
        </Box>
      </DialogContent>

      {/* Actions */}
      <DialogActions
        sx={{
          borderTop: `1px solid ${theme.palette.divider}`,
          px: 3,
          py: 2,
        }}
      >
        {allowSkip && (
          <Button
            onClick={handleSkip}
            disabled={isProcessing}
            sx={{ mr: 'auto' }}
          >
            Skip Cropping
          </Button>
        )}
        <Button onClick={onCancel} disabled={isProcessing}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleCropAndContinue}
          disabled={isProcessing || !croppedAreaPixels}
          startIcon={isProcessing ? <CircularProgress size={16} color="inherit" /> : <CropIcon />}
        >
          {isProcessing ? 'Processing...' : 'Crop & Continue'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImageCropModal;
